import React from 'react'
import {
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Slider,
  Box,
  MenuItem,
  Tooltip,
  IconButton,
  Chip
} from '@mui/material'
import { Help as HelpIcon } from '@mui/icons-material'
import { MILES_REPUBLIC_CONNECTION_ID } from '@data-agents/types'

// Interface pour le schema de configuration (ancien format)
interface ConfigFieldSchemaLegacy {
  type: 'text' | 'number' | 'boolean' | 'password' | 'select' | 'textarea' | 'database_select' | 'multiselect' | 'switch' | 'slider'
  label: string
  description?: string
  helpText?: string
  required?: boolean
  default?: any
  defaultValue?: any
  min?: number
  max?: number
  step?: number
  options?: Array<{ value: any; label: string }>
  rows?: number
  placeholder?: string
  validation?: {
    pattern?: string
    message?: string
    required?: boolean
    min?: number
    max?: number
    step?: number
  }
  category?: string
  advanced?: boolean
  order?: number
}

// Interface pour le nouveau format de champ
interface ConfigField {
  name: string
  type: string
  label: string
  description?: string
  helpText?: string
  required?: boolean
  defaultValue?: any
  options?: Array<{ value: any; label: string }>
  validation?: {
    required?: boolean
    min?: number
    max?: number
    step?: number
    pattern?: string
  }
  category?: string
}

// Interface pour les catégories
interface ConfigCategory {
  id: string
  label: string
  description?: string
}

// Interface pour le nouveau format de schéma
interface NewConfigSchema {
  title?: string
  description?: string
  fields: ConfigField[]
  categories?: ConfigCategory[]
}

// Type unifié pour le schéma
type ConfigSchema = { [fieldName: string]: ConfigFieldSchemaLegacy } | NewConfigSchema

interface DynamicConfigFormProps {
  configSchema?: ConfigSchema
  values: any
  onChange: (field: string, value: any) => void
  errors?: { [field: string]: string }
  touched?: { [field: string]: boolean }
}

// Normalise un champ du nouveau format vers l'ancien format interne
function normalizeFieldToLegacy(field: ConfigField): ConfigFieldSchemaLegacy {
  return {
    type: field.type as any,
    label: field.label,
    description: field.description || field.helpText,
    required: field.required ?? field.validation?.required,
    default: field.defaultValue,
    defaultValue: field.defaultValue,
    min: field.validation?.min,
    max: field.validation?.max,
    step: field.validation?.step,
    options: field.options,
    validation: field.validation,
    category: field.category
  }
}

const DynamicConfigForm: React.FC<DynamicConfigFormProps> = ({
  configSchema = {},
  values,
  onChange,
  errors = {},
  touched = {}
}) => {

  // Normaliser le schéma pour supporter les deux formats
  const normalizedSchema = React.useMemo(() => {
    // Vérifier si c'est le nouveau format (avec fields array)
    if ('fields' in configSchema && Array.isArray((configSchema as NewConfigSchema).fields)) {
      const newSchema = configSchema as NewConfigSchema
      const result: { [fieldName: string]: ConfigFieldSchemaLegacy } = {}

      newSchema.fields.forEach((field) => {
        result[field.name] = normalizeFieldToLegacy(field)
      })

      return result
    }

    // Sinon, c'est l'ancien format - le retourner tel quel
    // Filtrer les propriétés non-champ (title, description, categories)
    const { title, description, categories, fields, ...fieldConfigs } = configSchema as any
    return fieldConfigs as { [fieldName: string]: ConfigFieldSchemaLegacy }
  }, [configSchema])

  // Grouper les champs par catégorie et les trier par ordre
  const groupedFields = React.useMemo(() => {
    const groups: { [category: string]: Array<[string, ConfigFieldSchemaLegacy]> } = {}

    // Convertir en array et trier par ordre si défini
    const sortedEntries = Object.entries(normalizedSchema).sort(([, a], [, b]) => {
      const orderA = a.order ?? 1000 // Valeurs sans order en fin
      const orderB = b.order ?? 1000
      return orderA - orderB
    })

    sortedEntries.forEach(([fieldName, fieldConfig]) => {
      const category = fieldConfig.category || 'Configuration'
      if (!groups[category]) {
        groups[category] = []
      }
      groups[category].push([fieldName, fieldConfig])
    })

    return groups
  }, [normalizedSchema])

  const renderField = (fieldName: string, fieldConfig: ConfigFieldSchemaLegacy) => {
    const value = values[fieldName] ?? fieldConfig.default ?? fieldConfig.defaultValue ?? ''
    const hasError = touched[fieldName] && Boolean(errors[fieldName])
    const errorMessage = hasError ? errors[fieldName] : undefined

    // Helper pour les props communes
    const commonProps = {
      fullWidth: true,
      error: hasError,
      helperText: errorMessage || fieldConfig.description,
      variant: 'outlined' as const,
      value,
      onChange: (e: any) => {
        let newValue = e.target.value

        // Conversion de types
        if (fieldConfig.type === 'number' || fieldConfig.type === 'slider') {
          newValue = newValue === '' ? undefined : Number(newValue)
        } else if (fieldConfig.type === 'boolean' || fieldConfig.type === 'switch') {
          newValue = e.target.checked
        }

        onChange(fieldName, newValue)
      }
    }

    switch (fieldConfig.type) {
      case 'switch':
      case 'boolean':
        return (
          <FormControlLabel
            key={fieldName}
            control={
              <Switch
                checked={Boolean(value)}
                onChange={commonProps.onChange}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>{fieldConfig.label}</Typography>
                {fieldConfig.description && (
                  <Tooltip title={fieldConfig.description}>
                    <IconButton size="small">
                      <HelpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            }
            sx={{ mt: 1 }}
          />
        )

      case 'number':
        if (fieldConfig.min !== undefined && fieldConfig.max !== undefined && fieldConfig.max - fieldConfig.min <= 100) {
          // Utiliser un slider pour les plages de valeurs raisonnables
          return (
            <Box key={fieldName} sx={{ mt: 2, mb: 3 }}>
              <Typography gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {fieldConfig.label}
                {fieldConfig.description && (
                  <Tooltip title={fieldConfig.description}>
                    <IconButton size="small">
                      <HelpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Typography>
              <Box sx={{ px: 2, mt: 4 }}>
                <Slider
                  value={Number(value) || fieldConfig.default || fieldConfig.min || 0}
                  onChange={(_, newValue) => onChange(fieldName, newValue)}
                  min={fieldConfig.min}
                  max={fieldConfig.max}
                  step={fieldConfig.step || 1}
                  marks
                  valueLabelDisplay="on"
                  color={hasError ? 'error' : 'primary'}
                />
              </Box>
              {hasError && (
                <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                  {errorMessage}
                </Typography>
              )}
            </Box>
          )
        } else {
          // TextField numérique standard
          return (
            <TextField
              key={fieldName}
              {...commonProps}
              type="number"
              label={fieldConfig.label}
              InputProps={{
                inputProps: {
                  min: fieldConfig.min,
                  max: fieldConfig.max,
                  step: fieldConfig.step || 1
                },
                endAdornment: fieldConfig.description ? (
                  <Tooltip title={fieldConfig.description}>
                    <IconButton size="small">
                      <HelpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : undefined
              }}
            />
          )
        }

      case 'password':
        return (
          <TextField
            key={fieldName}
            {...commonProps}
            type="password"
            label={fieldConfig.label}
            placeholder={fieldConfig.placeholder}
            InputProps={{
              endAdornment: fieldConfig.description ? (
                <Tooltip title={fieldConfig.description}>
                  <IconButton size="small">
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : undefined
            }}
          />
        )

      case 'slider':
        // Slider explicite (nouveau format de schéma)
        return (
          <Box key={fieldName} sx={{ mt: 2, mb: 3 }}>
            <Typography gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {fieldConfig.label}
              {fieldConfig.description && (
                <Tooltip title={fieldConfig.description}>
                  <IconButton size="small">
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Typography>
            <Box sx={{ px: 2, mt: 4 }}>
              <Slider
                value={Number(value) || fieldConfig.default || fieldConfig.defaultValue || fieldConfig.min || 0}
                onChange={(_, newValue) => onChange(fieldName, newValue)}
                min={fieldConfig.min}
                max={fieldConfig.max}
                step={fieldConfig.step || 0.05}
                marks
                valueLabelDisplay="on"
                color={hasError ? 'error' : 'primary'}
              />
            </Box>
            {hasError && (
              <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                {errorMessage}
              </Typography>
            )}
          </Box>
        )

      case 'select':
        // Pour les champs database, peupler les options avec les bases de données
        // MR-3171 — il n'y a plus de table de connexions : la seule base
        // source est Miles Republic, résolue depuis MILES_REPUBLIC_DATABASE_URL.
        let selectOptions = fieldConfig.options || []
        const isDatabaseField = fieldName === 'sourceDatabase' || fieldName === 'milesRepublicDatabase'
        if (isDatabaseField) {
          selectOptions = [{ value: MILES_REPUBLIC_CONNECTION_ID, label: 'Miles Republic' }]
        }

        return (
          <TextField
            key={fieldName}
            {...commonProps}
            select
            label={fieldConfig.label}
            InputProps={{
              endAdornment: fieldConfig.description ? (
                <Tooltip title={fieldConfig.description}>
                  <IconButton size="small">
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : undefined
            }}
          >
            {selectOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )

      case 'database_select':
        // MR-3171 — la table `database_connections` a été supprimée. La seule
        // base source est Miles Republic, résolue depuis
        // MILES_REPUBLIC_DATABASE_URL côté serveur : il n'y a plus rien à
        // choisir, le champ affiche la valeur et reste en lecture seule.
        return (
          <TextField
            key={fieldName}
            {...commonProps}
            select
            label={fieldConfig.label}
            helperText={errorMessage || fieldConfig.description}
            InputProps={{
              endAdornment: fieldConfig.description ? (
                <Tooltip title={fieldConfig.description}>
                  <IconButton size="small">
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : undefined
            }}
          >
            <MenuItem value={MILES_REPUBLIC_CONNECTION_ID}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>Miles Republic</Typography>
                <Chip size="small" label="MILES_REPUBLIC" variant="outlined" />
              </Box>
            </MenuItem>
          </TextField>
        )

      case 'multiselect':
        const selectedValues = Array.isArray(value) ? value : []
        const multiselectOptions = fieldConfig.options || []

        return (
          <Box key={fieldName} sx={{ mb: 2 }}>
            <Typography gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500 }}>
              {fieldConfig.label}
              {fieldConfig.description && (
                <Tooltip title={fieldConfig.description}>
                  <IconButton size="small">
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: 1 }}>
              {multiselectOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Switch
                      checked={selectedValues.includes(option.value)}
                      onChange={(e) => {
                        const newValues = e.target.checked
                          ? [...selectedValues, option.value]
                          : selectedValues.filter((v: any) => v !== option.value)
                        onChange(fieldName, newValues)
                      }}
                      color="primary"
                      size="small"
                    />
                  }
                  label={option.label}
                  sx={{
                    border: '1px solid',
                    borderColor: selectedValues.includes(option.value) ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    m: 0,
                    bgcolor: selectedValues.includes(option.value) ? 'action.selected' : 'transparent'
                  }}
                />
              ))}
            </Box>
            {hasError && (
              <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5, display: 'block' }}>
                {errorMessage}
              </Typography>
            )}
            {fieldConfig.description && !hasError && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2, mt: 0.5, display: 'block' }}>
                {fieldConfig.description}
              </Typography>
            )}
          </Box>
        )

      case 'textarea':
        return (
          <TextField
            key={fieldName}
            {...commonProps}
            multiline
            rows={fieldConfig.rows || 3}
            label={fieldConfig.label}
            placeholder={fieldConfig.placeholder}
            InputProps={{
              endAdornment: fieldConfig.description ? (
                <Tooltip title={fieldConfig.description}>
                  <IconButton size="small">
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : undefined
            }}
          />
        )


      case 'text':
      default:
        return (
          <TextField
            key={fieldName}
            {...commonProps}
            label={fieldConfig.label}
            placeholder={fieldConfig.placeholder}
            InputProps={{
              endAdornment: fieldConfig.description ? (
                <Tooltip title={fieldConfig.description}>
                  <IconButton size="small">
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : undefined
            }}
          />
        )
    }
  }

  if (Object.keys(configSchema).length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Aucun paramètre configurable défini pour cet agent
        </Typography>
      </Box>
    )
  }

  // Fonction pour capitaliser la première lettre d'une catégorie
  const capitalizeCategory = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1)
  }

  // Fonction pour normaliser un type de champ
  const normalizeFieldType = (type: string, fieldConfig?: ConfigFieldSchemaLegacy) => {
    // Pour les nombres avec min/max qui forment une petite plage, c'est un slider
    if (type === 'number' && fieldConfig?.min !== undefined && fieldConfig?.max !== undefined && (fieldConfig.max - fieldConfig.min) <= 100) {
      return 'slider'
    }
    // Regrouper text, password et number comme "input"
    if (['text', 'password', 'number'].includes(type)) {
      return 'input'
    }
    // Les autres types (select, switch, slider, etc.) gardent leur propre type
    return type
  }

  // Fonction pour regrouper les champs de façon intelligente
  // Les champs du même type normalisé sont groupés ensemble sur une ou deux colonnes
  const groupFieldsByType = (fields: Array<[string, ConfigFieldSchemaLegacy]>) => {
    // Regrouper les champs par type normalisé
    const typeGroups: { [type: string]: Array<[string, ConfigFieldSchemaLegacy]> } = {}

    fields.forEach(([fieldName, fieldConfig]) => {
      const normalizedType = normalizeFieldType(fieldConfig.type || 'text', fieldConfig)
      if (!typeGroups[normalizedType]) {
        typeGroups[normalizedType] = []
      }
    typeGroups[normalizedType].push([fieldName, fieldConfig])
    })

    return typeGroups
  }

  // Fonction pour créer des paires de champs (pour layout 2 colonnes)
  const createFieldPairs = (fields: Array<[string, ConfigFieldSchemaLegacy]>, fieldType: string) => {
    // Pour les champs simples (input) et sliders, on peut les mettre 2 par 2
    // Pour les autres types (select, switch), chacun prend une ligne complète
    if (fieldType === 'input' || fieldType === 'slider') {
      const pairs: Array<Array<[string, ConfigFieldSchemaLegacy]>> = []
      for (let i = 0; i < fields.length; i += 2) {
        pairs.push(fields.slice(i, i + 2))
      }
      return pairs
    } else {
      // Un champ par ligne pour les autres types (select, switch, boolean, etc.)
      return fields.map(field => [field])
    }
  }


  return (
    <Box>
      {Object.entries(groupedFields).map(([category, fields]) => {
        const fieldsByType = groupFieldsByType(fields)

        return (
          <Box key={category} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Typography variant="h6" color="primary">
                {capitalizeCategory(category)}
              </Typography>
              <Chip
                size="small"
                label={`${fields.length} paramètre${fields.length > 1 ? 's' : ''}`}
                variant="outlined"
              />
            </Box>

            <Box>
              {Object.entries(fieldsByType).map(([fieldType, typeFields]) => {
                // Créer des paires de champs intelligentes
                const fieldPairs = createFieldPairs(typeFields, fieldType)

                return (
                  <Box key={fieldType} sx={{ mb: 3 }}>
                    {fieldPairs.map((pair, pairIndex) => (
                      <Grid container spacing={3} key={`${fieldType}-pair-${pairIndex}`} sx={{ mb: fieldPairs.length > pairIndex + 1 ? 0 : 0 }}>
                        {pair.map(([fieldName, fieldConfig]) => {
                          // Déterminer la taille de la grille
                          let gridSize = 12
                          // Si c'est un champ input ou slider et qu'il y a 2 champs dans la paire, utiliser 6
                          if ((fieldType === 'input' || fieldType === 'slider') && pair.length === 2) {
                            gridSize = 6
                          }
                          // Pour les autres types, toujours 12 (pleine largeur)

                          return (
                            <Grid item xs={12} md={gridSize} key={fieldName}>
                              {renderField(fieldName, fieldConfig)}
                            </Grid>
                          )
                        })}
                      </Grid>
                    ))}
                  </Box>
                )
              })}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default DynamicConfigForm
